import { FaCalendarAlt, FaDollarSign, FaFileAlt, FaUserTie, FaHandHoldingUsd, FaUserCheck, FaClock, FaHandshake } from 'react-icons/fa'
import ChromaGrid from '../components/ChromaGrid'
import { useRef } from 'react'
import VariableProximity from '../components/VariableProximity'
import GlassSurface from '../components/GlassSurface'

const Specialities = () => {
  const specialitiesHeadingRef = useRef(null)
  const whyChooseRef = useRef(null)
  const services = [
    {
      icon: <FaCalendarAlt className="text-5xl" />,
      title: 'Custom Weekly Scheduling',
      subtitle: 'Personalized Planning',
      description: 'We organize your week according to your preferences, ensuring you are never without a load. Your schedule, your way.',
      features: [
        'Personalized weekly planning',
        'Preference-based scheduling',
        'Continuous load availability',
        'Flexible time management',
      ],
      borderColor: '#3B82F6',
      gradient: 'linear-gradient(145deg, #3B82F6, #000)',
    },
    {
      icon: <FaDollarSign className="text-5xl" />,
      title: 'Maximized Earnings',
      subtitle: 'Best Rates Guaranteed',
      description: 'We negotiate the best-paying rates to ensure your desired gross is met. Your profitability is our priority.',
      features: [
        'Competitive rate negotiations',
        'Gross revenue targets met',
        'Industry-leading pay rates',
        'Revenue optimization',
      ],
      borderColor: '#06B6D4',
      gradient: 'linear-gradient(180deg, #06B6D4, #000)',
    },
    {
      icon: <FaFileAlt className="text-5xl" />,
      title: 'Streamlined Paperwork',
      subtitle: 'Complete Administrative Support',
      description: 'We handle everything from setting up accounts with brokers, submitting invoices for factoring, to obtaining necessary insurance certificates and permits.',
      features: [
        'Broker account setup',
        'Invoice factoring submission',
        'Insurance certificates',
        'Permit acquisition',
      ],
      borderColor: '#F59E0B',
      gradient: 'linear-gradient(165deg, #F59E0B, #000)',
    },
    {
      icon: <FaUserTie className="text-5xl" />,
      title: 'Dedicated Support',
      subtitle: 'Personal Dispatcher',
      description: 'Each carrier is assigned a personal dispatcher who monitors your calendar, preferences and time off. Your success is our mission.',
      features: [
        'Personal dispatcher assigned',
        'Calendar monitoring',
        'Preference tracking',
        'Time off management',
      ],
      borderColor: '#EAB308',
      gradient: 'linear-gradient(210deg, #EAB308, #000)',
    },
    {
      icon: <FaHandHoldingUsd className="text-5xl" />,
      title: 'Expense Coverage',
      subtitle: 'Financial Assistance',
      description: 'We assist with TONU, Lumper, Detention, and Layover costs. We help you manage and recover additional expenses.',
      features: [
        'TONU assistance',
        'Lumper cost coverage',
        'Detention fee support',
        'Layover cost management',
      ],
      borderColor: '#10B981',
      gradient: 'linear-gradient(195deg, #10B981, #000)',
    },
    {
      icon: <FaUserCheck className="text-5xl" />,
      title: 'No Force Dispatch',
      subtitle: 'You Stay In Control',
      description: 'You remain in full control of your schedule. No pressure, no forced loads. Your business, your decisions.',
      features: [
        'Full schedule control',
        'No forced dispatches',
        'Complete autonomy',
        'Respect for your choices',
      ],
      borderColor: '#EF4444',
      gradient: 'linear-gradient(225deg, #EF4444, #000)',
    },
    {
      icon: <FaClock className="text-5xl" />,
      title: '24/7 Dispatch Support',
      subtitle: 'Always Available',
      description: 'Assistance is always available, day or night. Round-the-clock support when you need it most.',
      features: [
        '24/7 availability',
        'Emergency support',
        'After-hours assistance',
        'Always on call',
      ],
      borderColor: '#8B5CF6',
      gradient: 'linear-gradient(240deg, #8B5CF6, #000)',
    },
    {
      icon: <FaHandshake className="text-5xl" />,
      title: 'Flexibility',
      subtitle: 'No Contract Obligations',
      description: 'There are no contract obligations and you are free to discontinue at any time. Work with us on your terms.',
      features: [
        'No long-term contracts',
        'Cancel anytime',
        'Flexible terms',
        'No obligations',
      ],
      borderColor: '#EC4899',
      gradient: 'linear-gradient(255deg, #EC4899, #000)',
    },
  ]

  return (
    <div>
      {/* Header */}
      <section className="section-padding text-white pt-24 border-b border-dark-700/30">
        <div className="container-custom">
          <div 
            className="text-center rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(12px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div
              ref={specialitiesHeadingRef}
              style={{ position: 'relative' }}
              className="mb-4"
            >
              <VariableProximity
                label="Our Services"
                className="text-5xl font-bold"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={specialitiesHeadingRef}
                radius={100}
                falloff="linear"
                style={{ color: 'white' }}
              />
            </div>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              We specialize in Freight Consultancy and strive to provide the best possible services to our carriers. 
              Our focus is on alleviating the pressure of load sourcing and negotiations, ensuring that your vehicle 
              is continuously moving with the most competitive rates in the industry.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div style={{ minHeight: '600px', position: 'relative' }}>
            <ChromaGrid 
              items={services}
              radius={300}
              damping={0.45}
              fadeOut={0.6}
              ease="power3.out"
              columns={4}
              rows={2}
            />
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div
              ref={whyChooseRef}
              style={{ position: 'relative' }}
              className="mb-6"
            >
              <VariableProximity
                label="Why Choose EW Logistics & IT Solution LLC?"
                className="text-3xl font-bold text-white"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={whyChooseRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <p className="text-lg text-gray-300 mb-8">
              We are committed to providing exceptional freight consultancy services that help your business thrive. 
              Our team understands the challenges you face and works tirelessly to ensure your vehicle stays moving 
              with the most competitive rates. We do not just find loads—we build partnerships and maximize your profitability.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">24/7</div>
                  <div className="text-gray-300">Dispatch Support</div>
                </div>
              </GlassSurface>
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">Best</div>
                  <div className="text-gray-300">Competitive Rates</div>
                </div>
              </GlassSurface>
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">No</div>
                  <div className="text-gray-300">Contract Required</div>
                </div>
              </GlassSurface>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Specialities

